import { economyFetch as fetch } from '../../lib/economyClient';
/**
 * パックシステム - サーバーAPI統合版
 *
 * packSystem.ts と同じインターフェースを提供しつつ、
 * サーバーAPIを使用してパック開封処理を行う
 *
 * 使用方法:
 * import { packSystemServer } from './packSystemServer';
 * const result = await packSystemServer.buyAndOpenPackAsync(playerData, allCards);
 */

// ========================================
// 定数
// ========================================

const API_TIMEOUT_MS = 30000; // 30秒タイムアウト

// Supabase設定（supabase.functions.invokeはハングするためREST APIを直接使用）
const SUPABASE_URL = 'https://vikqkqwklbdjfhkydzpw.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZpa3FrcXdrbGJkamZoa3lkenB3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwNjYzODcsImV4cCI6MjA4MTY0MjM4N30.PPIf98mu1HgR95CaymSJiV9E5hnlsMJuQeXw2MNqKsw';
const AUTH_STORAGE_KEY = 'sb-vikqkqwklbdjfhkydzpw-auth-token';

/**
 * localStorageからアクセストークンを取得
 */
function getAccessToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    console.log('[PackAPI] getAccessToken - stored:', stored ? `${stored.substring(0, 50)}...` : 'null');
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log('[PackAPI] getAccessToken - parsed keys:', Object.keys(parsed));
      console.log('[PackAPI] getAccessToken - has access_token:', !!parsed?.access_token);
      return parsed?.access_token || null;
    }
    console.log('[PackAPI] getAccessToken - no stored value');
  } catch (err) {
    console.error('[PackAPI] Failed to get access token:', err);
  }
  return null;
}

// ========================================
// ユーティリティ
// ========================================

/**
 * タイムアウト付きPromiseラッパー
 */
function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`${operation}: タイムアウト（${ms}ms）`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timeout);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
}

// ========================================
// 型定義
// ========================================

type PackType = 'normal' | 'fire' | 'water' | 'light' | 'dark' | 'primitive' | 'future' | 'royal';

interface DrawnCard {
  cardId: string;
  rarity: string;
  card: Record<string, unknown>;
  isNew?: boolean;
}

interface PackResult {
  success: boolean;
  error?: string;
  cards?: DrawnCard[];
  playerData?: Record<string, unknown>;
  packCount?: number;
  packType?: string;
}

// ========================================
// 属性名マッピング
// ========================================

const ATTRIBUTE_TO_PACK_TYPE: Record<string, PackType> = {
  '炎': 'fire',
  '水': 'water',
  '光': 'light',
  '闇': 'dark',
  '原始': 'primitive',
  '未来': 'future',
};

// ========================================
// サーバーAPI呼び出し
// ========================================

/**
 * サーバーAPIでパックを開封
 * @param packType パックタイプ
 * @param count 開封数
 * @param useUnopened 未開封パックを使用するか
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
async function callPackOpenAPI(
  packType: PackType,
  count: number,
  useUnopened: boolean,
  accessToken?: string | null
): Promise<{
  success: boolean;
  error?: string;
  cards?: Array<{ cardId: string; rarity: string; isNew?: boolean }>;
  goldSpent: number;
  newGoldBalance: number;
  packsOpened: number;
}> {
  console.log('[PackAPI] Calling pack open API:', { packType, count, useUnopened });
  const startTime = Date.now();

  try {
    // アクセストークン取得（引数優先、なければlocalStorageから）
    const token = accessToken || getAccessToken();
    if (!token) {
      console.error('[PackAPI] No access token found');
      return {
        success: false,
        error: '認証が必要です。ページをリロードしてください。',
        goldSpent: 0,
        newGoldBalance: 0,
        packsOpened: 0,
      };
    }
    console.log('[PackAPI] Using token from:', accessToken ? 'argument' : 'localStorage');

    // REST API直接呼び出し（supabase.functions.invokeはハングする問題があるため）
    const apiCall = fetch(`${SUPABASE_URL}/functions/v1/collection/pack/open`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        pack_type: packType,
        count,
        use_unopened: useUnopened,
      }),
    });

    const response = await withTimeout(
      apiCall,
      API_TIMEOUT_MS,
      'パック開封API'
    );

    const elapsed = Date.now() - startTime;
    console.log('[PackAPI] Response received:', { elapsed: `${elapsed}ms`, status: response.status });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[PackAPI] HTTP error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('[PackAPI] Response data:', data);

    if (!data?.success) {
      console.warn('[PackAPI] API returned failure:', data);
      return {
        success: false,
        error: data?.error || 'パック開封に失敗しました',
        goldSpent: 0,
        newGoldBalance: 0,
        packsOpened: 0,
      };
    }

    console.log('[PackAPI] Success:', { cards: data.cards?.length, goldSpent: data.gold_spent });
    return {
      success: true,
      cards: data.cards || [],
      goldSpent: data.gold_spent || 0,
      newGoldBalance: data.new_gold_balance || 0,
      packsOpened: data.packs_opened || count,
    };
  } catch (err) {
    const elapsed = Date.now() - startTime;
    console.error('[PackAPI] Error after', elapsed, 'ms:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'サーバーエラー',
      goldSpent: 0,
      newGoldBalance: 0,
      packsOpened: 0,
    };
  }
}

/**
 * サーバーレスポンスをpackSystem形式に変換
 */
function convertToPackSystemFormat(
  apiResult: Awaited<ReturnType<typeof callPackOpenAPI>>,
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  packCount: number = 1,
  packType: string = 'normal'
): PackResult {
  if (!apiResult.success) {
    return {
      success: false,
      error: apiResult.error,
    };
  }

  // カードデータをマッピング
  const cardMap = new Map<string, Record<string, unknown>>();
  for (const card of allCards) {
    cardMap.set(card.id as string, card);
  }

  const cards: DrawnCard[] = (apiResult.cards || []).map(c => ({
    cardId: c.cardId,
    rarity: c.rarity,
    card: cardMap.get(c.cardId) || { id: c.cardId, name: 'Unknown' },
    isNew: c.isNew,
  }));

  // コレクションも更新（サーバーで既に追加されているが、ローカルにも反映）
  const collection = [...((playerData.collection as Array<Record<string, unknown>>) || [])];
  for (const card of cards) {
    const existing = collection.find(
      c => c.cardId === card.cardId && c.rarity === card.rarity
    );
    if (existing) {
      (existing.quantity as number)++;
    } else {
      collection.push({
        cardId: card.cardId,
        rarity: card.rarity,
        quantity: 1,
      });
    }
  }

  // playerDataを更新（サーバーの結果を反映）
  const newPlayerData = {
    ...playerData,
    gold: apiResult.newGoldBalance,
    stats: {
      ...(playerData.stats as Record<string, unknown> || {}),
      packsOpened: ((playerData.stats as Record<string, unknown>)?.packsOpened as number || 0) + packCount,
      totalGoldSpent: ((playerData.stats as Record<string, unknown>)?.totalGoldSpent as number || 0) + apiResult.goldSpent,
    },
    collection,
  };

  return {
    success: true,
    cards,
    playerData: newPlayerData,
    packCount,
    packType,
  };
}

// ========================================
// 通常パック
// ========================================

/**
 * パックを購入して開封（サーバーAPI版）
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
export async function buyAndOpenPackAsync(
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  _cardValueMap: unknown = null,
  accessToken?: string | null
): Promise<PackResult> {
  const apiResult = await callPackOpenAPI('normal', 1, false, accessToken);
  return convertToPackSystemFormat(apiResult, playerData, allCards, 1, 'normal');
}

/**
 * 複数パックを購入して開封（サーバーAPI版）
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
export async function buyAndOpenMultiplePacksAsync(
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  packCount: number,
  _cardValueMap: unknown = null,
  accessToken?: string | null
): Promise<PackResult> {
  const apiResult = await callPackOpenAPI('normal', packCount, false, accessToken);
  return convertToPackSystemFormat(apiResult, playerData, allCards, packCount, 'normal');
}

/**
 * 未開封パックを開封（サーバーAPI版）
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
export async function openUnopenedPackAsync(
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  _cardValueMap: unknown = null,
  accessToken?: string | null
): Promise<PackResult> {
  const apiResult = await callPackOpenAPI('normal', 1, true, accessToken);

  if (!apiResult.success) {
    return {
      success: false,
      error: apiResult.error,
    };
  }

  const result = convertToPackSystemFormat(apiResult, playerData, allCards, 1, 'normal');

  // 未開封パック数を減らす
  if (result.playerData) {
    result.playerData.unopenedPacks = Math.max(0, (playerData.unopenedPacks as number || 0) - 1);

    // unopenedPacksByTypeも更新
    const byType = { ...(playerData.unopenedPacksByType as Record<string, number> || {}) };
    if (byType.normal && byType.normal > 0) {
      byType.normal--;
    }
    result.playerData.unopenedPacksByType = byType;
  }

  return result;
}

/**
 * 複数の未開封パックを開封（サーバーAPI版）
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
export async function openMultipleUnopenedPacksAsync(
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  packCount: number,
  _cardValueMap: unknown = null,
  accessToken?: string | null
): Promise<PackResult> {
  const apiResult = await callPackOpenAPI('normal', packCount, true, accessToken);

  if (!apiResult.success) {
    return {
      success: false,
      error: apiResult.error,
    };
  }

  const result = convertToPackSystemFormat(apiResult, playerData, allCards, packCount, 'normal');

  // 未開封パック数を減らす
  if (result.playerData) {
    result.playerData.unopenedPacks = Math.max(0, (playerData.unopenedPacks as number || 0) - packCount);

    const byType = { ...(playerData.unopenedPacksByType as Record<string, number> || {}) };
    if (byType.normal) {
      byType.normal = Math.max(0, byType.normal - packCount);
    }
    result.playerData.unopenedPacksByType = byType;
  }

  return result;
}

// ========================================
// 属性別パック
// ========================================

/**
 * 属性パックを購入して開封（サーバーAPI版）
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
export async function buyAndOpenAttributePackAsync(
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  attribute: string,
  _cardValueMap: unknown = null,
  accessToken?: string | null
): Promise<PackResult> {
  const packType = ATTRIBUTE_TO_PACK_TYPE[attribute];
  if (!packType) {
    return {
      success: false,
      error: `無効な属性: ${attribute}`,
    };
  }

  const apiResult = await callPackOpenAPI(packType, 1, false, accessToken);
  return convertToPackSystemFormat(apiResult, playerData, allCards, 1, attribute);
}

// ========================================
// ロイヤルパック
// ========================================

/**
 * ロイヤルパックを購入して開封（サーバーAPI版）
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
export async function buyAndOpenRoyalPackAsync(
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  _cardValueMap: unknown = null,
  accessToken?: string | null
): Promise<PackResult> {
  const apiResult = await callPackOpenAPI('royal', 1, false, accessToken);
  return convertToPackSystemFormat(apiResult, playerData, allCards, 1, 'royal');
}

/**
 * 複数ロイヤルパックを購入して開封（サーバーAPI版）
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
export async function buyAndOpenMultipleRoyalPacksAsync(
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  packCount: number,
  _cardValueMap: unknown = null,
  accessToken?: string | null
): Promise<PackResult> {
  const apiResult = await callPackOpenAPI('royal', packCount, false, accessToken);
  return convertToPackSystemFormat(apiResult, playerData, allCards, packCount, 'royal');
}

// ========================================
// タイプ別未開封パック
// ========================================

/**
 * タイプ別未開封パックを開封（サーバーAPI版）
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
export async function openTypedUnopenedPackAsync(
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  packType: string,
  _cardValueMap: unknown = null,
  accessToken?: string | null
): Promise<PackResult> {
  // packTypeを正規化
  let normalizedType: PackType = 'normal';
  if (packType === 'normal' || packType === 'royal') {
    normalizedType = packType;
  } else if (ATTRIBUTE_TO_PACK_TYPE[packType]) {
    normalizedType = ATTRIBUTE_TO_PACK_TYPE[packType];
  } else if (['fire', 'water', 'light', 'dark', 'primitive', 'future'].includes(packType)) {
    normalizedType = packType as PackType;
  }

  const apiResult = await callPackOpenAPI(normalizedType, 1, true, accessToken);

  if (!apiResult.success) {
    return {
      success: false,
      error: apiResult.error,
    };
  }

  const result = convertToPackSystemFormat(apiResult, playerData, allCards, 1, packType);

  // 未開封パック数を減らす
  if (result.playerData) {
    const byType = { ...(playerData.unopenedPacksByType as Record<string, number> || {}) };
    if (byType[normalizedType] && byType[normalizedType] > 0) {
      byType[normalizedType]--;
    }
    result.playerData.unopenedPacksByType = byType;
  }

  return result;
}

/**
 * 複数のタイプ別未開封パックを開封（サーバーAPI版）
 * @param accessToken アクセストークン（省略時はlocalStorageから取得）
 */
export async function openMultipleTypedUnopenedPacksAsync(
  playerData: Record<string, unknown>,
  allCards: Array<Record<string, unknown>>,
  packType: string,
  packCount: number,
  _cardValueMap: unknown = null,
  accessToken?: string | null
): Promise<PackResult> {
  // packTypeを正規化
  let normalizedType: PackType = 'normal';
  if (packType === 'normal' || packType === 'royal') {
    normalizedType = packType;
  } else if (ATTRIBUTE_TO_PACK_TYPE[packType]) {
    normalizedType = ATTRIBUTE_TO_PACK_TYPE[packType];
  } else if (['fire', 'water', 'light', 'dark', 'primitive', 'future'].includes(packType)) {
    normalizedType = packType as PackType;
  }

  const apiResult = await callPackOpenAPI(normalizedType, packCount, true, accessToken);

  if (!apiResult.success) {
    return {
      success: false,
      error: apiResult.error,
    };
  }

  const result = convertToPackSystemFormat(apiResult, playerData, allCards, packCount, packType);

  // 未開封パック数を減らす
  if (result.playerData) {
    const byType = { ...(playerData.unopenedPacksByType as Record<string, number> || {}) };
    if (byType[normalizedType]) {
      byType[normalizedType] = Math.max(0, byType[normalizedType] - packCount);
    }
    result.playerData.unopenedPacksByType = byType;
  }

  return result;
}

// ========================================
// エクスポート
// ========================================

export const packSystemServer = {
  // 通常パック
  buyAndOpenPackAsync,
  buyAndOpenMultiplePacksAsync,
  openUnopenedPackAsync,
  openMultipleUnopenedPacksAsync,
  // 属性パック
  buyAndOpenAttributePackAsync,
  // ロイヤルパック
  buyAndOpenRoyalPackAsync,
  buyAndOpenMultipleRoyalPacksAsync,
  // タイプ別未開封パック
  openTypedUnopenedPackAsync,
  openMultipleTypedUnopenedPacksAsync,
};

export default packSystemServer;
